import React, { useState, useEffect } from 'react';
import {
  Paper,
  LinearProgress,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useProgressBar } from '../../hooks/useProgressBar';
import styles from './styles.module.css';

const ProgressBarFloating = () => {
  const { progress, closeProgress } = useProgressBar();
  const { open, current, total, status, message, errorMessage } = progress;
  const [autoClosePercent, setAutoClosePercent] = useState(0);

  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  // Timer de auto-fechamento após sucesso (5 segundos)
  useEffect(() => {
    if (!open || status !== 'success') {
      setAutoClosePercent(0);
      return;
    }

    setAutoClosePercent(0);
    const totalMs = 5000;
    const stepMs = 100;
    let elapsed = 0;

    const intervalId = setInterval(() => {
      elapsed += stepMs;
      const pct = Math.min(100, Math.round((elapsed / totalMs) * 100));
      setAutoClosePercent(pct);

      if (elapsed >= totalMs) {
        clearInterval(intervalId);
        closeProgress();
      }
    }, stepMs);

    return () => clearInterval(intervalId);
  }, [open, status, closeProgress]);

  if (!open) return null;

  return (
    <Paper elevation={6} className={styles.progressContainer}>
      <Box className={styles.progressContent}>
        <Box className={styles.progressHeader}>
          <Typography className={styles.progressTitle}>
            {status === 'processing' && 'Processando créditos na Conta Corrente...'}
            {status === 'success' && 'Créditos criados com sucesso!'}
            {status === 'error' && 'Erro ao criar créditos'}
          </Typography>
          {status !== 'processing' && (
            <IconButton size="small" onClick={closeProgress}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {status === 'processing' && (
          <>
            <Box className={styles.progressBar}>
              <Box className={styles.progressInfo}>
                <Typography variant="body2" color="text.secondary">
                  {message || `Processando ${current}/${total} créditos...`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {percentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percentage}
              />
            </Box>
            <Typography className={styles.progressMessage}>
              Você pode continuar navegando enquanto os créditos são processados.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <Box className={styles.successContainer}>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {total} crédito(s) criado(s) com sucesso!
              </Typography>
              {message && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {message}
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Este aviso será fechado automaticamente em 5 segundos.
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={autoClosePercent}
                  sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {status === 'error' && (
          <Box className={styles.errorContainer}>
            <ErrorIcon sx={{ color: 'error.main', fontSize: 32 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                Erro ao processar créditos
              </Typography>
              {errorMessage && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ProgressBarFloating;
